import { PropertiesPanelPropertyConfig } from '../../../../../widgets/propertiesPanel';
import React from 'react';
import { T } from 'src/shared/helpers';

export type ObjectConfigHelpers = {
    sceneEditor: any;
    updateProperty: (updates: any) => void;
    onPropertyChanged?: (property: string, value: any) => void;
    isVTOEnabledInScene: boolean;
    fineTuningSettings?: any;
    handleVirtualTryonFinetuning: () => void;
    handleResetFinetuningSettings: () => void;
};

export type ObjectNode = {
    id: string;
    name: string;
    position: [number, number, number];
    rotation: [number, number, number];
    scaling: [number, number, number];
    scaleSlider: number;
    materialOptions: { value: string; label: string }[];
    selectedMaterial: { value: string; label: string };
    showInAR: boolean;
    enableInVTO: boolean;
    metadata?: { [key: string]: any };
    data?: any;
};

const createPropertyConfig = (config: PropertiesPanelPropertyConfig) => config;

// Helper per formattare numeri molto piccoli o molto grandi
const formatNumberValue = (value: number): string => {
    if (Math.abs(value) < 0.0001) {
        return value.toExponential(3);
    } else if (Math.abs(value) < 0.01) {
        return value.toFixed(6);
    } else {
        return value.toFixed(3);
    }
};

// Configurazione per un input triplo (x, y, z)
const createTripleNumberConfig = (
    id: string,
    label: string,
    values: [number, number, number],
    onChange: (axis: 'x' | 'y' | 'z', value: number) => void
): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'point',
        id,
        label,
        coords: [
            {
                id: 'x',
                label: 'X',
                value: values[0],
                onChange: (newValue) => onChange('x', newValue)
            },
            {
                id: 'y',
                label: 'Y',
                value: values[1],
                onChange: (newValue) => onChange('y', newValue)
            },
            {
                id: 'z',
                label: 'Z',
                value: values[2],
                onChange: (newValue) => onChange('z', newValue)
            }
        ]
    });
};

// Configurazione per il gruppo delle proprietà di base
const createBasicPropertiesGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'basic-properties',
        header: T._('Basic Properties', 'Admin'),
        defaultIsOpen: true,
        properties: [
            // Nome
            createPropertyConfig({
                type: 'text',
                id: 'name',
                label: T._('Name', 'Admin'),
                value: node.name,
                onChange: (newValue) => {
                    const validatedName = (newValue || '').trim();
                    helpers.updateProperty({ name: validatedName });
                }
            }),

            // Position
            createTripleNumberConfig('position', T._('Position', 'Admin'), node.position, (axis, value) => {
                const newPosition = [...node.position] as [number, number, number];
                const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
                newPosition[axisIndex] = value;
                helpers.updateProperty({ position: newPosition });
            }),

            // Rotation
            createTripleNumberConfig('rotation', T._('Rotation', 'Admin'), node.rotation, (axis, value) => {
                const newRotation = [...node.rotation] as [number, number, number];
                const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
                newRotation[axisIndex] = value;
                helpers.updateProperty({ rotation: newRotation });
            }),

            // Scaling
            createTripleNumberConfig('scaling', T._('Scaling', 'Admin'), node.scaling, (axis, value) => {
                const newScaling = [...node.scaling] as [number, number, number];
                const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
                newScaling[axisIndex] = value;
                helpers.updateProperty({ scaling: newScaling });
            }),

            // Scaling Slider
            createPropertyConfig({
                type: 'range',
                id: 'scale-slider',
                label: T._('Scaling Slider', 'Admin'),
                value: node.scaleSlider,
                min: 0.001,
                max: 10,
                step: 0.001,
                onChange: (value) => {
                    helpers.updateProperty({
                        scaleSlider: value,
                        scaling: [value, value, value]
                    });
                }
            })
        ]
    });
};

// Configurazione per il gruppo dei materiali
const createMaterialsGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'materials',
        header: T._('Materials', 'Admin'),
        defaultIsOpen: false,
        properties: [
            createPropertyConfig({
                type: 'select',
                id: 'material-select',
                label: T._('Materials', 'Admin'),
                options: node.materialOptions,
                value: node.selectedMaterial,
                onChange: (val) => {
                    if (val) {
                        helpers.updateProperty({ selectedMaterial: val });
                    }
                }
            })
        ]
    });
};

// Configurazione per il gruppo AR
const createARGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'ar-settings',
        header: T._('AR Settings', 'Admin'),
        defaultIsOpen: false,
        properties: [
            createPropertyConfig({
                type: 'checkbox',
                id: 'show-in-ar',
                label: T._('Show in AR', 'Admin'),
                checked: node.showInAR,
                onChange: (checked) => {
                    helpers.updateProperty({ showInAR: checked });
                }
            })
        ]
    });
};

// Configurazione per il gruppo VTO
const createVTOGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig | null => {
    if (!helpers.isVTOEnabledInScene) {
        return null;
    }

    return createPropertyConfig({
        type: 'group',
        id: 'vto-settings',
        header: T._('VTO Settings', 'Admin'),
        defaultIsOpen: false,
        properties: [
            createPropertyConfig({
                type: 'checkbox',
                id: 'enable-in-vto',
                label: T._('Enable in VTO', 'Admin'),
                checked: node.enableInVTO && helpers.isVTOEnabledInScene,
                onChange: (checked) => {
                    helpers.updateProperty({ enableInVTO: checked });
                }
            })
        ]
    });
};

// Configurazione per il gruppo Fine Tuning (solo per nodi root o transform)
const createFineTuningGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig | null => {
    const isRootNode = node.metadata?.isRootNode === true;
    const isTransformNode = node.metadata?.isTransformNode === true;

    if (!helpers.isVTOEnabledInScene || (!isRootNode && !isTransformNode)) {
        return null;
    }

    const properties = [];

    // Info sui settings esistenti solo per nodi root
    if (isRootNode && helpers.fineTuningSettings) {
        properties.push(
            createPropertyConfig({
                type: 'text',
                id: 'finetuning-info',
                label: T._('Status', 'Admin'),
                value: '✅ Finetuning settings loaded',
                disabled: true
            })
        );
    }

    properties.push(
        createPropertyConfig({
            type: 'icon',
            id: 'virtual-tryon-finetuning',
            label: T._('Virtual Try-on Finetuning', 'Admin'),
            icon: <span>🎯</span>,
            onIconClick: helpers.handleVirtualTryonFinetuning
        }),
        createPropertyConfig({
            type: 'icon',
            id: 'reset-finetuning',
            label: T._('Reset Finetuning Settings', 'Admin'),
            icon: <span>🔄</span>,
            onIconClick: helpers.handleResetFinetuningSettings
        })
    );

    return createPropertyConfig({
        type: 'group',
        id: 'finetuning-settings',
        header: T._('Fine Tuning', 'Admin'),
        defaultIsOpen: false,
        properties
    });
};

// Funzione principale che determina quale configurazione usare in base al tipo di nodo
const objectPropertiesByType = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig[] => {
    const baseProperties = [
        createBasicPropertiesGroup(node, helpers),
        createMaterialsGroup(node, helpers),
        createARGroup(node, helpers),
        createVTOGroup(node, helpers),
        createFineTuningGroup(node, helpers)
    ];

    return baseProperties.filter(Boolean) as PropertiesPanelPropertyConfig[];
};

// Hook principale da esportare (simile a useMaterialsPropertiesCreator)
export const useObjectPropertiesCreator = (): ((node: ObjectNode, helpers: ObjectConfigHelpers) => PropertiesPanelPropertyConfig[]) => {
    return (node: ObjectNode, helpers: ObjectConfigHelpers) => {
        return objectPropertiesByType(node, helpers);
    };
};
